import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import meta from '@build-meta';
import { EMPTY, Observable, Subscription, fromEvent, interval, merge } from 'rxjs';
import { catchError, exhaustMap, filter, map } from 'rxjs/operators';

import { AppStorageService } from './app-storage.service';

export const BUILD_DATE = new InjectionToken<number>('BUILD_DATE', {
  providedIn: 'root',
  factory: () => Number(meta.buildDate) || 0
});

export const BUILD_CHECK_INTERVAL_MS = 15 * 60 * 1000;
const RELOAD_STORAGE_KEY = 'lastReloadForBuild';
const MAX_RECORDED_BUILDS = 10;

@Injectable({
  providedIn: 'root'
})
export class CacheBusterService implements OnDestroy {
  private router = inject(Router);
  private buildDate = inject(BUILD_DATE);
  private storage = inject(AppStorageService);

  // Built on HttpBackend so the check skips the app's interceptors: no bearer token on a
  // static file, and no 401 refresh or sign-out when the check fails.
  private readonly http: HttpClient;
  private checks?: Subscription;
  /** A build found by a background check; the page reloads for it on the next navigation. */
  private pendingBuildDate: number | null = null;

  constructor() {
    const httpBackend = inject(HttpBackend);

    this.http = new HttpClient(httpBackend);
  }

  init(): void {
    this.clearInitialCache();

    // Only a navigation reloads: the timer and the tab becoming visible can fire while an
    // unsaved form is open, so they just remember that a new build is waiting.
    const navigations = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => true)
    );
    const backgroundChecks = merge(
      fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible')),
      interval(BUILD_CHECK_INTERVAL_MS)
    ).pipe(map(() => false));

    this.checks?.unsubscribe();
    this.checks = merge(navigations, backgroundChecks)
      .pipe(exhaustMap(isNavigation => (isNavigation ? this.onNavigation() : this.checkForNewBuild(false))))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.checks?.unsubscribe();
  }

  checkForNewBuild(reloadWhenDifferent = true): Observable<void> {
    return this.http
      .get<{ buildDate?: unknown }>(`/assets/meta.json?${Date.now()}`, { headers: { 'Cache-Control': 'no-cache' } })
      .pipe(
        map(served => this.handleServedBuild(Number(served?.buildDate), reloadWhenDifferent)),
        catchError(err => {
          console.warn('Build version check failed:', err);
          return EMPTY;
        })
      );
  }

  private onNavigation(): Observable<void> {
    const pendingBuildDate = this.pendingBuildDate;
    if (pendingBuildDate === null) {
      return this.checkForNewBuild();
    }
    this.pendingBuildDate = null;
    this.reloadForBuild(pendingBuildDate);
    return EMPTY;
  }

  private handleServedBuild(servedBuildDate: number, reloadWhenDifferent: boolean): void {
    this.pendingBuildDate = null;
    if (!Number.isFinite(servedBuildDate) || servedBuildDate <= 0 || servedBuildDate === this.buildDate) {
      return;
    }
    if (reloadWhenDifferent) {
      this.reloadForBuild(servedBuildDate);
    } else {
      this.pendingBuildDate = servedBuildDate;
    }
  }

  private reloadForBuild(servedBuildDate: number): void {
    // A stale index.html, or two origins serving different builds, would otherwise reload forever:
    // reload at most once per build per session, and not at all when that can't be recorded.
    const reloadedBuilds = this.readReloadedBuilds();
    if (reloadedBuilds === null || reloadedBuilds.includes(servedBuildDate)) {
      return;
    }
    if (!this.writeReloadedBuilds([...reloadedBuilds, servedBuildDate].slice(-MAX_RECORDED_BUILDS))) {
      return;
    }

    const cleared =
      'caches' in window
        ? Promise.resolve()
            .then(() => caches.keys())
            .then(keys => Promise.all(keys.map(key => caches.delete(key))))
        : Promise.resolve();
    cleared.catch(err => console.warn('Cache clearing failed:', err)).finally(() => this.reloadPage());
  }

  private reloadPage(): void {
    location.reload();
  }

  private readReloadedBuilds(): number[] | null {
    try {
      return (this.storage.get('session', RELOAD_STORAGE_KEY) || '')
        .split(',')
        .map(Number)
        .filter(buildDate => Number.isFinite(buildDate) && buildDate > 0);
    } catch {
      return null;
    }
  }

  private writeReloadedBuilds(buildDates: number[]): boolean {
    try {
      const value = buildDates.join(',');
      this.storage.set('session', RELOAD_STORAGE_KEY, value);
      return this.storage.get('session', RELOAD_STORAGE_KEY) === value;
    } catch {
      return false;
    }
  }

  private clearInitialCache(): void {
    const isCleared = this.storage.get('local', 'initialCacheCleared');

    if (isCleared !== 'Yes') {
      if ('caches' in window) {
        caches.keys().then(keys => {
          Promise.all(keys.map(key => caches.delete(key)))
            .then(() => {
              this.storage.set('local', 'initialCacheCleared', 'Yes');
            })
            .catch(err => console.error('Initial cache clearing failed:', err));
        });
      }
    }
  }
}
